import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { identityApi } from '@/api/identity.api';
import { PortalDataList } from './PortalDataList';
import { showApiError } from '@/utils/apiError';

export function EmployeePortalSections() {
  const { pathname } = useLocation();
  const section = pathname.split('/').pop() ?? 'dashboard';

  if (section === 'leave') return <EmployeeLeaveSection />;
  if (section === 'payroll') return <EmployeePayrollSection />;
  if (section === 'profile' || section === 'attendance') return <EmployeeProfileSection />;
  if (section === 'claims' || section === 'documents' || section === 'training' || section === 'performance' || section === 'approvals') {
    return <PortalDataList title={section.charAt(0).toUpperCase() + section.slice(1)} items={[]} emptyMessage="No items yet." />;
  }
  return <EmployeeProfileSection />;
}

function EmployeeProfileSection() {
  const [profile, setProfile] = useState<{ name?: string; code?: string } | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    void identityApi.employeeProfile()
      .then((p) => setProfile({
        name: [p.employee?.firstName, p.employee?.lastName].filter(Boolean).join(' '),
        code: p.employee?.employeeCode,
      }))
      .catch((e) => showApiError(e, 'Could not load profile'))
      .finally(() => setLoading(false));
  }, []);

  return (
    <PortalDataList
      title="My profile"
      loading={loading}
      items={profile ? [{
        id: 1,
        primary: profile.name ?? 'Employee',
        secondary: profile.code ? `Code: ${profile.code}` : undefined,
      }] : []}
    />
  );
}

function EmployeePayrollSection() {
  const [summary, setSummary] = useState<Record<string, unknown> | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    void identityApi.employeePayrollSummary()
      .then(setSummary)
      .catch((e) => showApiError(e, 'Could not load payroll'))
      .finally(() => setLoading(false));
  }, []);

  return (
    <PortalDataList
      title="Payroll"
      loading={loading}
      items={summary ? [{
        id: 1,
        primary: summary.employeeCode ? `Employee ${String(summary.employeeCode)}` : 'Payroll summary',
        secondary: summary.message ? String(summary.message) : undefined,
      }] : []}
      emptyMessage="Payroll information is not available yet."
    />
  );
}

function EmployeeLeaveSection() {
  const [items, setItems] = useState<Array<{ id: number; primary: string; meta: string }>>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    void identityApi.employeeLeaveBalances()
      .then((balances) => setItems(balances.map((b) => ({
        id: b.id,
        primary: `Leave type ${b.leaveTypeId}`,
        meta: `${b.balanceDays ?? 0} days`,
      }))))
      .catch((e) => showApiError(e, 'Could not load leave balances'))
      .finally(() => setLoading(false));
  }, []);

  return (
    <PortalDataList
      title="Leave balances"
      loading={loading}
      items={items}
      emptyMessage="No leave balances configured."
    />
  );
}
